import * as Popover from '@radix-ui/react-popover';
import {
  AlertTriangle,
  Bell,
  Bot,
  CalendarCheck,
  CalendarClock,
  Flame,
  Inbox,
  Repeat,
  Undo2,
  UserPlus,
  UserMinus,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { fmt } from '@/lib/format';
import { useMarkRead, useNotifications } from '@/lib/queries';

const ICON = {
  LEAD_ASSIGNED: UserPlus,
  LEAD_NEW: UserPlus,
  TASK_OVERDUE_DIGEST: CalendarClock,
  HANDOFF: Bot,
  LEAD_REASSIGNED_AWAY: UserMinus,
  ATTENTION_PRIORITY: Flame,
  ATTENTION_RESUMED: Bot,
  BOT_APPOINTMENT: CalendarClock,
  BOT_BUDGET: Wallet,
  APPOINTMENT_CONFIRMATION: CalendarCheck,
  APPOINTMENT_CHANGED: CalendarClock,
  LEAD_RECONTACT: Repeat,
  DEAL_UNDONE: Undo2,
  UNIT_TAKEN: AlertTriangle,
  POOL_WAITING: Inbox,
  ADVISOR_HANDOVER: UserMinus,
} as const;

export function NotificationsBell() {
  const { data } = useNotifications();
  const markRead = useMarkRead();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const unread = data?.unreadCount ?? 0;
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={`Notificaciones${unread ? ` (${unread} sin leer)` : ''}`}
          data-testid="notifications-bell"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="bg-danger absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="bg-card text-card-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-50 w-80 rounded-lg border shadow-lg"
        >
          <div className="flex items-center justify-between border-b px-3 py-2">
            <p className="text-sm font-semibold">Notificaciones</p>
            {unread > 0 && (
              <button
                type="button"
                className="text-primary text-xs underline"
                onClick={() => markRead.mutate({ all: true })}
              >
                Marcar todo leído
              </button>
            )}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {data?.items.length === 0 && (
              <li className="text-muted-foreground p-4 text-sm">Sin notificaciones.</li>
            )}
            {data?.items.map((n) => {
              const Icon = ICON[n.type] ?? Bell;
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    className={`hover:bg-accent flex w-full gap-3 px-3 py-2 text-left text-sm ${n.readAt ? 'opacity-70' : ''}`}
                    onClick={() => {
                      if (!n.readAt) markRead.mutate({ ids: [n.id] });
                      setOpen(false);
                      if (n.leadId) navigate(`/leads/${n.leadId}`);
                      // El resumen de la bolsa no tiene lead: lleva a Equipo → Bolsa de su empresa.
                      else if (n.type === 'POOL_WAITING')
                        navigate(
                          `/equipo?tab=bolsa${n.companyId ? `&companyId=${n.companyId}` : ''}`,
                        );
                      else navigate('/');
                    }}
                  >
                    <Icon
                      className={`mt-0.5 h-4 w-4 shrink-0 ${n.readAt ? 'text-muted-foreground' : 'text-primary'}`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium">{n.title}</span>
                      {n.body && (
                        <span className="text-muted-foreground block truncate text-xs">
                          {n.body}
                        </span>
                      )}
                      <span className="text-muted-foreground block text-[11px]">
                        {fmt.ago(n.createdAt)}
                      </span>
                    </span>
                    {!n.readAt && (
                      <span className="bg-primary mt-2 h-2 w-2 shrink-0 rounded-full" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
